<template>
  <div class="presales-handover">
    <EntraPageHeader
      title="Atlas Workplace Presales Handover"
      :showTenantInfo="false"
      :showBreadcrumb="false"
      :showSearch="false"
      :showSavedViews="false"
      :showExport="false"
      :showRefresh="false"
    />

    <div class="page-body">
      <div v-if="loading" class="loading-state">Loading form definition...</div>
      <div v-if="loadError" class="error-message">{{ loadError }}</div>

      <template v-if="definition && !submitted">
        <div class="wizard-layout">
          <aside class="stepper">
            <div class="stepper-count">Section {{ currentIndex + 1 }} of {{ visibleSections.length }}</div>
            <div class="stepper-progress">
              <div class="stepper-progress-fill" :style="{ width: progressPercent + '%' }"></div>
            </div>
            <ol class="stepper-list">
              <li v-for="(section, index) in visibleSections" :key="section.id">
                <button
                  type="button"
                  class="stepper-item"
                  :class="{
                    'stepper-active': index === currentIndex,
                    'stepper-done': index !== currentIndex && sectionAnswered(section),
                    'stepper-invalid': index !== currentIndex && sectionHasErrors(section)
                  }"
                  @click="goToSection(index)"
                >
                  <span class="stepper-badge">
                    <svg v-if="index !== currentIndex && sectionAnswered(section) && !sectionHasErrors(section)" class="stepper-check" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4l3.3 3.29 7.3-7.29a1 1 0 011.4 0z" clip-rule="evenodd"/>
                    </svg>
                    <span v-else>{{ index + 1 }}</span>
                  </span>
                  <span class="stepper-title">{{ section.title }}</span>
                </button>
              </li>
            </ol>
          </aside>

          <div class="step-panel card">
            <header class="panel-header">
              <h2 class="panel-title">{{ currentSection.title }}</h2>
            </header>

            <div v-if="currentSection.note" class="note-callout">
              <svg class="note-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clip-rule="evenodd"/>
              </svg>
              <p>{{ currentSection.note }}</p>
            </div>

            <div v-if="currentSection.id === 'migration-scope' && !migrationProfile" class="gate-note">
              <div class="gate-note-text">
                Migration is not in scope for this build profile — these fields are blank by default.
                You can adjust them manually if required.
              </div>
              <label class="gate-toggle">
                <input type="checkbox" v-model="migrationOverride" />
                Adjust migration scope manually
              </label>
            </div>

            <div v-if="currentMissing.length > 0" class="error-message" role="alert">
              Please answer: {{ currentMissing.join(', ') }}
            </div>

            <div
              v-for="field in currentVisibleFields"
              :key="field.id"
              class="form-field"
              :class="{ 'field-invalid': isFieldMissing(field), 'field-locked': isFieldLocked(field) }"
            >
              <span class="field-label">
                {{ field.label }}
                <span v-if="field.required" class="required-mark" aria-hidden="true">*</span>
              </span>

              <div v-if="field.type === 'single'" class="option-grid" role="radiogroup" :aria-label="field.label">
                <label
                  v-for="option in field.options"
                  :key="option.value"
                  class="option-card"
                  :class="{ 'option-selected': answers[field.id] === option.value }"
                >
                  <input
                    type="radio"
                    :name="field.id"
                    :value="option.value"
                    :checked="answers[field.id] === option.value"
                    :disabled="isFieldLocked(field)"
                    @change="setSingle(field.id, option.value)"
                  />
                  <span>{{ option.label }}</span>
                </label>
              </div>

              <div v-else-if="field.type === 'multi'" class="option-grid">
                <label
                  v-for="option in field.options"
                  :key="option.value"
                  class="option-card"
                  :class="{ 'option-selected': (answers[field.id] || []).indexOf(option.value) !== -1 }"
                >
                  <input
                    type="checkbox"
                    :value="option.value"
                    :checked="(answers[field.id] || []).indexOf(option.value) !== -1"
                    :disabled="isFieldLocked(field)"
                    @change="toggleMulti(field.id, option.value)"
                  />
                  <span>{{ option.label }}</span>
                </label>
              </div>

              <textarea
                v-else-if="field.multiline"
                class="text-input"
                rows="4"
                :placeholder="field.placeholder || ''"
                :value="answers[field.id] || ''"
                :disabled="isFieldLocked(field)"
                @input="setText(field.id, $event.target.value)"
              ></textarea>
              <input
                v-else
                type="text"
                class="text-input"
                :placeholder="field.placeholder || ''"
                :value="answers[field.id] || ''"
                :disabled="isFieldLocked(field)"
                @input="setText(field.id, $event.target.value)"
              />

              <div
                v-if="field.id === 'dbTicket' && jiraTicket.state !== 'idle'"
                class="jira-ticket-card"
                :class="'jira-state-' + jiraTicket.state"
              >
                <span v-if="jiraTicket.state === 'loading'" class="jira-ticket-note">Looking up ticket in Jira...</span>
                <template v-else-if="jiraTicket.state === 'found' && jiraTicket.ticket">
                  <div class="jira-ticket-head">
                    <a class="jira-ticket-key" :href="jiraTicket.ticket.url" target="_blank" rel="noopener">{{ jiraTicket.ticket.key }}</a>
                    <span v-if="jiraTicket.ticket.status" class="jira-ticket-status">{{ jiraTicket.ticket.status }}</span>
                    <span v-if="jiraTicket.ticket.issueType" class="jira-ticket-type">{{ jiraTicket.ticket.issueType }}</span>
                  </div>
                  <div v-if="jiraTicket.ticket.summary" class="jira-ticket-summary">{{ jiraTicket.ticket.summary }}</div>
                  <div class="jira-ticket-meta">
                    <span v-if="jiraTicket.ticket.assignee">Assignee: {{ jiraTicket.ticket.assignee }}</span>
                    <span v-if="jiraTicket.ticket.reporter">Reporter: {{ jiraTicket.ticket.reporter }}</span>
                  </div>
                  <div
                    v-if="(jiraTicket.ticket.labels && jiraTicket.ticket.labels.length) || (jiraTicket.ticket.components && jiraTicket.ticket.components.length)"
                    class="jira-ticket-tags"
                  >
                    <span v-for="label in jiraTicket.ticket.labels || []" :key="'label-' + label" class="jira-tag">{{ label }}</span>
                    <span v-for="component in jiraTicket.ticket.components || []" :key="'component-' + component" class="jira-tag jira-tag-component">{{ component }}</span>
                  </div>
                </template>
                <span v-else class="jira-ticket-note">{{ jiraTicket.message }}</span>
              </div>
            </div>

            <footer class="panel-footer">
              <router-link to="/presales/submissions" class="ghost-link">View submissions</router-link>
              <div class="footer-actions">
                <button
                  type="button"
                  class="secondary-button"
                  :disabled="currentIndex === 0"
                  @click="goBack"
                >
                  Back
                </button>
                <button
                  v-if="!isLastSection"
                  type="button"
                  class="primary-button"
                  @click="goNext"
                >
                  Next
                </button>
                <button
                  v-else
                  type="button"
                  class="primary-button"
                  :disabled="submitting"
                  @click="submit"
                >
                  {{ submitting ? 'Submitting...' : 'Submit Handover' }}
                </button>
              </div>
            </footer>
          </div>
        </div>
      </template>

      <div v-if="submitted" class="card submitted-card">
        <svg class="submitted-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.6 7.7 9.3a1 1 0 00-1.4 1.4l2 2a1 1 0 001.4 0l4-4z" clip-rule="evenodd"/>
        </svg>
        <h3>Handover submitted</h3>
        <p>{{ submitted.customerName }} (ticket <code>{{ submitted.dbTicket }}</code>) was recorded.</p>
        <div class="submit-bar">
          <button type="button" class="secondary-button" @click="startNew">Start another handover</button>
          <router-link :to="'/presales/submissions/' + submitted.id" class="primary-button as-link">
            View submission
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import { getJiraTicket, getPresalesFormDefinition, submitPresalesHandover } from '@/services/presales-api.js';

const HIDDEN_WHEN_ENTRA_ONLY = ['adHosting', 'adScope', 'identitySync', 'connectivity', 'migrationActivities'];
const MIGRATION_GATED_FIELDS = [
  'dataMigration',
  'dataMigrationSummary',
  'serverSource',
  'serverTarget',
  'deliveryScenario',
  'buildScenarios',
  'serverTypes'
];
const DATA_MIGRATION_FIELDS = ['dataMigration', 'dataMigrationSummary', 'serverSource'];
const OFFICE_DETAIL_FIELDS = ['officeDesignDeviation', 'officeLocations'];

export default {
  name: 'PresalesHandoverForm',
  components: { EntraPageHeader },
  data() {
    return {
      definition: null,
      loading: false,
      loadError: null,
      answers: {},
      currentIndex: 0,
      attemptedSections: [],
      submitting: false,
      submitted: null,
      migrationOverride: false,
      jiraTicket: { state: 'idle', ticket: null, message: null },
      jiraLookupTimer: null,
      jiraLookupSequence: 0
    };
  },
  beforeUnmount() {
    if (this.jiraLookupTimer) {
      clearTimeout(this.jiraLookupTimer);
      this.jiraLookupTimer = null;
    }
  },
  watch: {
    visibleSections(newValue) {
      if (this.currentIndex > newValue.length - 1) {
        this.currentIndex = Math.max(0, newValue.length - 1);
      }
    }
  },
  computed: {
    sections() {
      return this.definition ? this.definition.sections : [];
    },
    visibleSections() {
      return this.sections.filter((section) => this.isSectionVisible(section));
    },
    currentSection() {
      return this.visibleSections[this.currentIndex] || { title: '', fields: [] };
    },
    isLastSection() {
      return this.currentIndex === this.visibleSections.length - 1;
    },
    progressPercent() {
      if (this.visibleSections.length === 0) return 0;
      return Math.round(((this.currentIndex + 1) / this.visibleSections.length) * 100);
    },
    currentMissing() {
      if (this.attemptedSections.indexOf(this.currentSection.id) === -1) return [];
      return this.missingForSection(this.currentSection);
    },
    platformsInScope() {
      const value = this.answers.platformsInScope;
      return Array.isArray(value) ? value : [];
    },
    migrationProfile() {
      const profile = this.answers.buildProfile;
      return profile === 'greenfield-with-migration' || profile === 'brownfield-with-migration';
    },
    migrationLockedSet() {
      return MIGRATION_GATED_FIELDS;
    },
    currentVisibleFields() {
      const fields = this.currentSection.fields || [];
      return fields.filter((field) => this.isFieldVisible(field));
    }
  },
  mounted() {
    this.loadDefinition();
  },
  methods: {
    async loadDefinition() {
      this.loading = true;
      this.loadError = null;
      try {
        this.definition = await getPresalesFormDefinition();
      } catch (error) {
        this.loadError = error.message;
      } finally {
        this.loading = false;
      }
    },
    setSingle(fieldId, value) {
      if (fieldId === 'buildProfile' && typeof value === 'string' && !value.endsWith('-with-migration')) {
        this.migrationOverride = false;
      }
      this.answers = { ...this.answers, [fieldId]: value };
    },
    toggleMulti(fieldId, value) {
      const current = Array.isArray(this.answers[fieldId]) ? this.answers[fieldId].slice() : [];
      const index = current.indexOf(value);
      if (index === -1) {
        current.push(value);
      } else {
        current.splice(index, 1);
      }
      this.answers = { ...this.answers, [fieldId]: current };
    },
    setText(fieldId, value) {
      this.answers = { ...this.answers, [fieldId]: value };
      if (fieldId === 'dbTicket') {
        this.scheduleJiraLookup();
      }
    },
    scheduleJiraLookup() {
      if (this.jiraLookupTimer) {
        clearTimeout(this.jiraLookupTimer);
        this.jiraLookupTimer = null;
      }
      this.jiraLookupSequence += 1;
      const value = String(this.answers.dbTicket || '').trim();
      if (!value) {
        this.jiraTicket = { state: 'idle', ticket: null, message: null };
        return;
      }
      const sequence = this.jiraLookupSequence;
      this.jiraLookupTimer = setTimeout(() => {
        this.jiraLookupTimer = null;
        this.runJiraLookup(value, sequence);
      }, 600);
    },
    async runJiraLookup(value, sequence) {
      const candidate = value.match(/^[A-Za-z][A-Za-z0-9]*-\d+$/) || value.match(/\/browse\/([A-Za-z][A-Za-z0-9]*-\d+)/i);
      if (!candidate) {
        this.jiraTicket = { state: 'idle', ticket: null, message: null };
        return;
      }
      const key = (candidate[1] || candidate[0]).toUpperCase();
      this.jiraTicket = { state: 'loading', ticket: null, message: null };
      try {
        const result = await getJiraTicket(key);
        if (sequence !== this.jiraLookupSequence) return;
        this.jiraTicket = { state: 'found', ticket: result.ticket, message: null };
        if (result.ticket && result.ticket.key && this.answers.dbTicket !== result.ticket.key) {
          this.answers = { ...this.answers, dbTicket: result.ticket.key };
        }
      } catch (error) {
        if (sequence !== this.jiraLookupSequence) return;
        const message = error.code === 'JIRA_RATE_LIMITED'
          ? 'Jira is rate limiting lookups — try again shortly.'
          : error.message;
        this.jiraTicket = { state: 'error', ticket: null, message: message };
      }
    },
    isSectionVisible(section) {
      if (section.id === 'server-estate') {
        return this.platformsInScope.indexOf('no-infrastructure') === -1;
      }
      if (section.id === 'azure-networking' || section.id === 'backup-dr') {
        return this.platformsInScope.indexOf('azure') !== -1;
      }
      return true;
    },
    isFieldVisible(field) {
      if (HIDDEN_WHEN_ENTRA_ONLY.indexOf(field.id) !== -1 && this.answers.identityArchitecture === 'entra-only') {
        return false;
      }
      if (field.id === 'azurePlanDetails' && this.answers.azureModel === 'no-existing-azure') {
        return false;
      }
      if (DATA_MIGRATION_FIELDS.indexOf(field.id) !== -1) {
        const complexity = Array.isArray(this.answers.primaryComplexity) ? this.answers.primaryComplexity : [];
        if (complexity.indexOf('data-migration') === -1) return false;
      }
      if (OFFICE_DETAIL_FIELDS.indexOf(field.id) !== -1 && this.answers.officeDesign === 'none-required') {
        return false;
      }
      return true;
    },
    isFieldLocked(field) {
      return !this.migrationProfile && !this.migrationOverride && MIGRATION_GATED_FIELDS.indexOf(field.id) !== -1;
    },
    fieldEmpty(value) {
      return (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0)
      );
    },
    missingForSection(section) {
      const missing = [];
      for (const field of section.fields) {
        if (!this.isFieldVisible(field) || this.isFieldLocked(field)) continue;
        if (field.required && this.fieldEmpty(this.answers[field.id])) {
          missing.push(field.label);
        }
      }
      return missing;
    },
    sectionAnswered(section) {
      return section.fields.some(
        (field) => this.isFieldVisible(field) && !this.isFieldLocked(field) && !this.fieldEmpty(this.answers[field.id])
      );
    },
    sectionHasErrors(section) {
      return this.attemptedSections.indexOf(section.id) !== -1 && this.missingForSection(section).length > 0;
    },
    isFieldMissing(field) {
      return (
        this.attemptedSections.indexOf(this.currentSection.id) !== -1 &&
        field.required &&
        this.isFieldVisible(field) &&
        !this.isFieldLocked(field) &&
        this.fieldEmpty(this.answers[field.id])
      );
    },
    markAttempted(section) {
      if (this.attemptedSections.indexOf(section.id) === -1) {
        this.attemptedSections = [...this.attemptedSections, section.id];
      }
    },
    goToSection(index) {
      if (index < this.currentIndex) {
        this.currentIndex = index;
        this.scrollPanelTop();
        return;
      }
      for (let i = this.currentIndex; i < index; i++) {
        this.markAttempted(this.visibleSections[i]);
        if (this.missingForSection(this.visibleSections[i]).length > 0) {
          this.currentIndex = i;
          this.scrollPanelTop();
          return;
        }
      }
      this.currentIndex = index;
      this.scrollPanelTop();
    },
    goBack() {
      if (this.currentIndex > 0) {
        this.currentIndex -= 1;
        this.scrollPanelTop();
      }
    },
    goNext() {
      this.markAttempted(this.currentSection);
      if (this.missingForSection(this.currentSection).length > 0) {
        this.scrollPanelTop();
        return;
      }
      if (this.currentIndex < this.visibleSections.length - 1) {
        this.currentIndex += 1;
        this.scrollPanelTop();
      }
    },
    scrollPanelTop() {
      this.$nextTick(() => {
        const panel = this.$el && this.$el.querySelector('.step-panel');
        if (panel && typeof panel.scrollIntoView === 'function') {
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    buildSubmitAnswers() {
      const cleaned = {};
      for (const section of this.visibleSections) {
        for (const field of section.fields) {
          if (field.id === 'customerName' || field.id === 'dbTicket') continue;
          if (!this.isFieldVisible(field) || this.isFieldLocked(field)) continue;
          const value = this.answers[field.id];
          if (!this.fieldEmpty(value)) cleaned[field.id] = value;
        }
      }
      return cleaned;
    },
    async submit() {
      let firstInvalid = -1;
      for (let i = 0; i < this.visibleSections.length; i++) {
        this.markAttempted(this.visibleSections[i]);
        if (firstInvalid === -1 && this.missingForSection(this.visibleSections[i]).length > 0) {
          firstInvalid = i;
        }
      }
      if (firstInvalid !== -1) {
        this.currentIndex = firstInvalid;
        this.scrollPanelTop();
        return;
      }
      this.submitting = true;
      try {
        const result = await submitPresalesHandover({
          customerName: String(this.answers.customerName || '').trim(),
          dbTicket: String(this.answers.dbTicket || '').trim(),
          answers: this.buildSubmitAnswers()
        });
        this.submitted = result.submission;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (error) {
        if (error.details && Array.isArray(error.details.missing) && error.details.missing.length > 0) {
          const missingIds = error.details.missing.map((entry) => entry.fieldId);
          for (let i = 0; i < this.visibleSections.length; i++) {
            const section = this.visibleSections[i];
            if (section.fields.some((field) => missingIds.indexOf(field.id) !== -1)) {
              this.markAttempted(section);
              this.currentIndex = i;
              break;
            }
          }
          this.scrollPanelTop();
        } else {
          this.loadError = error.message;
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } finally {
        this.submitting = false;
      }
    },
    startNew() {
      this.answers = {};
      this.submitted = null;
      this.attemptedSections = [];
      this.currentIndex = 0;
      this.migrationOverride = false;
      this.jiraLookupSequence += 1;
      this.jiraTicket = { state: 'idle', ticket: null, message: null };
    }
  }
};
</script>

<style scoped>
.presales-handover {
  min-height: 100vh;
  background: #f8fafc;
}

.page-body {
  padding: 1.5rem 2rem 3rem;
  max-width: 1200px;
}

.wizard-layout {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 1.5rem;
  align-items: start;
}

.stepper {
  position: sticky;
  top: 1.5rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem;
}

.stepper-count {
  font-size: 0.75rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.stepper-progress {
  height: 6px;
  background: #eef2ff;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.stepper-progress-fill {
  height: 100%;
  background: #4f46e5;
  border-radius: 999px;
  transition: width 250ms ease;
}

.stepper-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.stepper-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  border-radius: 6px;
  padding: 0.45rem 0.5rem;
  cursor: pointer;
  color: #475569;
  font-size: 0.8125rem;
  font-family: inherit;
  transition: background 150ms ease, color 150ms ease;
}

.stepper-item:hover {
  background: #f8fafc;
  color: #0f172a;
}

.stepper-item:focus-visible {
  outline: 2px solid #4f46e5;
  outline-offset: 1px;
}

.stepper-badge {
  flex: 0 0 auto;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 999px;
  border: 1.5px solid #cbd5e1;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  transition: all 150ms ease;
}

.stepper-check {
  width: 0.85rem;
  height: 0.85rem;
}

.stepper-title {
  line-height: 1.25;
}

.stepper-active {
  background: #eef2ff;
  color: #0f172a;
  font-weight: 600;
}

.stepper-active .stepper-badge {
  border-color: #4f46e5;
  background: #4f46e5;
  color: #ffffff;
}

.stepper-done .stepper-badge {
  border-color: #4f46e5;
  background: #eef2ff;
  color: #4f46e5;
}

.stepper-invalid .stepper-badge {
  border-color: #dc2626;
  color: #dc2626;
  background: #fef2f2;
}

.card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.step-panel {
  padding: 1.75rem 2rem;
  scroll-margin-top: 1.5rem;
}

.panel-header {
  padding-bottom: 1rem;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 1.25rem;
}

.panel-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
}

.note-callout {
  display: flex;
  gap: 0.625rem;
  align-items: flex-start;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  padding: 0.75rem 0.875rem;
  margin-bottom: 1.25rem;
}

.note-callout p {
  margin: 0;
  font-size: 0.8125rem;
  color: #3730a3;
  line-height: 1.45;
}

.note-icon {
  flex: 0 0 auto;
  width: 1rem;
  height: 1rem;
  margin-top: 0.1rem;
  color: #4f46e5;
}

.form-field {
  margin-bottom: 1.5rem;
  border-radius: 8px;
  padding: 0.25rem;
  transition: background 150ms ease;
}

.gate-note {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
}

.gate-note-text {
  font-size: 0.8125rem;
  color: #92400e;
  line-height: 1.45;
  flex: 1 1 340px;
}

.gate-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #92400e;
  cursor: pointer;
}

.gate-toggle input {
  accent-color: #d97706;
}

.field-locked {
  opacity: 0.55;
  pointer-events: none;
}

.field-invalid {
  background: #fef2f2;
  outline: 1px solid #fecaca;
  padding: 0.75rem 1rem 0.875rem;
}

.field-label {
  display: block;
  font-size: 0.75rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 0.625rem;
}

.required-mark {
  color: #dc2626;
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.5rem;
}

.option-card {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  color: #1e293b;
  cursor: pointer;
  background: #ffffff;
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.option-card:hover {
  border-color: #c7d2fe;
  background: #fafbff;
}

.option-card:focus-within {
  outline: 2px solid #4f46e5;
  outline-offset: 1px;
}

.option-card input {
  margin-top: 0.15rem;
  accent-color: #4f46e5;
}

.option-selected {
  border-color: #4f46e5;
  background: #eef2ff;
  box-shadow: 0 0 0 1px #4f46e5 inset;
}

.text-input {
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  color: #1e293b;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.text-input:hover {
  border-color: #cbd5e1;
}

.text-input:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
}

.panel-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.25rem;
  border-top: 1px solid #f1f5f9;
  margin-top: 0.5rem;
}

.footer-actions {
  display: flex;
  gap: 0.75rem;
}

.ghost-link {
  color: #64748b;
  font-size: 0.8125rem;
  text-decoration: none;
  transition: color 150ms ease;
}

.ghost-link:hover {
  color: #4f46e5;
}

.primary-button {
  background: #4f46e5;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 0.625rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 150ms ease, box-shadow 150ms ease;
}

.primary-button:hover:not(:disabled) {
  background: #4338ca;
  box-shadow: 0 2px 6px rgba(79, 70, 229, 0.3);
}

.primary-button:focus-visible {
  outline: 2px solid #4f46e5;
  outline-offset: 2px;
}

.primary-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.primary-button.as-link {
  text-decoration: none;
  display: inline-block;
}

.secondary-button {
  background: #ffffff;
  color: #334155;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 150ms ease, background 150ms ease;
}

.secondary-button:hover:not(:disabled) {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.secondary-button:focus-visible {
  outline: 2px solid #4f46e5;
  outline-offset: 2px;
}

.secondary-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-state,
.empty-state {
  color: #64748b;
  padding: 2rem 0;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.875rem;
}

.jira-ticket-card {
  margin-top: 0.625rem;
  border: 1px solid #c7d2fe;
  background: #f5f7ff;
  border-radius: 8px;
  padding: 0.625rem 0.75rem;
  font-size: 0.8125rem;
}

.jira-state-error {
  border-color: #fecaca;
  background: #fef2f2;
}

.jira-ticket-note {
  color: #64748b;
}

.jira-state-error .jira-ticket-note {
  color: #dc2626;
}

.jira-ticket-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.jira-ticket-key {
  font-weight: 700;
  color: #4f46e5;
  text-decoration: none;
}

.jira-ticket-key:hover {
  text-decoration: underline;
}

.jira-ticket-status {
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 999px;
  padding: 0.1rem 0.55rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.jira-ticket-type {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
}

.jira-ticket-summary {
  margin-top: 0.375rem;
  color: #1e293b;
  font-weight: 600;
}

.jira-ticket-meta {
  margin-top: 0.25rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  color: #64748b;
  font-size: 0.75rem;
}

.jira-ticket-tags {
  margin-top: 0.5rem;
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
}

.jira-tag {
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  color: #3730a3;
  border-radius: 999px;
  padding: 0.05rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
}

.jira-tag-component {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #475569;
}

.submitted-card {
  max-width: 560px;
  margin: 2rem auto 0;
  padding: 2.5rem;
  text-align: center;
}

.submitted-icon {
  width: 3rem;
  height: 3rem;
  color: #16a34a;
  margin-bottom: 0.75rem;
}

.submitted-card h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  color: #0f172a;
}

.submitted-card p {
  color: #475569;
  margin: 0 0 1.5rem 0;
}

.submit-bar {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
}

@media (max-width: 900px) {
  .wizard-layout {
    grid-template-columns: 1fr;
  }

  .stepper {
    position: static;
  }

  .stepper-list {
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    max-height: none;
  }

  .stepper-item {
    flex: 0 0 auto;
    width: auto;
  }

  .stepper-title {
    display: none;
  }
}
</style>
